import { streamQuantityEventName } from '@/components/commonComponents/ExportByColumnModal';
import { QueryKeysEnum } from '@/enums';
import { ExtractionBatchEntity, PostExtractionBatchDto } from '@/interfaces';
import { newQueryKeys } from '@/queries/queryKeys';
import {
  AddExtractionSequences,
  ExtractionParams,
  MarkCompletedExtractionStatus,
  SendExtractionToSequencingBatch,
  SequencingBatchEntity,
  UploadExtractionSequenceDataFiles,
  ValidateExtractionSequenceConcentration,
  ValidationExtractionAllowDnaExtraction,
} from '@/types';
import { QueryFunctionContext } from '@tanstack/react-query';
import APIService from './axios.service';
import { BaseService } from './base.service';

class ExtractionBatchService extends BaseService<
  'extraction',
  ExtractionBatchEntity,
  PostExtractionBatchDto
> {
  constructor() {
    super(QueryKeysEnum.EXTRACTION); //path
  }

  sendToSequencingBatch = (
    payload: SendExtractionToSequencingBatch,
  ): Promise<SequencingBatchEntity> => {
    const { extractionBatchId, ...rest } = payload;
    return APIService.post(
      this.path + `/${extractionBatchId}/send-for-sequencing-batch`,
      rest,
    );
  };

  // Only used for export specific batches, NOT export all
  exportExtractionBatches = (
    context: QueryFunctionContext<
      ReturnType<(typeof newQueryKeys)['extractionExport']['list']>
    >,
  ): Promise<ExtractionBatchEntity[]> => {
    const { params } = context.queryKey[0];
    return APIService.get(this.path + '/export-data', { params });
  };

  streamExtractionBatch = (params: ExtractionParams) => {
    return APIService.get(this.path + '/stream-data', {
      params,
      responseType: 'stream',
      onDownloadProgress(data) {
        const responseData = data.event?.target?.response || '';
        // Regex to find all progress values
        const regex = /"progress"\s*:\s*(\d+(\.\d+)?)/g;
        const matches = [...responseData.matchAll(regex)];
        // Get the last match
        const lastMatch = matches[matches.length - 1];
        const lastProgress = lastMatch
          ? Math.round(parseFloat(lastMatch[1]))
          : 0;

        // Dispatch kit quantity for percent progress bar
        window.dispatchEvent(
          new CustomEvent(streamQuantityEventName, {
            detail: { percentProgress: lastProgress },
          }),
        );
      },
    });
  };

  validateBatchAllowDnaExtraction = (
    extractionBatchId: string,
  ): Promise<ValidationExtractionAllowDnaExtraction[]> => {
    return APIService.post(this.path + `/${extractionBatchId}/validate`);
  };

  markCompletedExtractionStatus = (
    payload: MarkCompletedExtractionStatus,
  ): Promise<void> => {
    const { extractionBatchId, ...rest } = payload;
    return APIService.put(
      this.path + `/${extractionBatchId}/mark-extracted`,
      rest,
    );
  };

  validateExtractionSequencesDataFiles = (
    payload: UploadExtractionSequenceDataFiles,
  ): Promise<ValidateExtractionSequenceConcentration[]> => {
    const { extractionBatchId, files } = payload;

    const formData = new FormData();

    files.forEach(file => formData.append('files', file));

    return APIService.post(
      this.path +
        `/${extractionBatchId}/validate-multiple-extraction-sequences`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
  };

  addExtractionSequences = (payload: AddExtractionSequences): Promise<void> => {
    const { extractionBatchId, ...rest } = payload;

    return APIService.post(
      this.path + `/${extractionBatchId}/extraction-sequences`,
      { ...rest },
    );
  };
}

export const extractionService = new ExtractionBatchService();
